'use client'

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router'
import securityManager from '../utils/security'
import { tempAccessManager } from '../utils/tempAccess'
import { verifyPassword } from '../utils/passwordHash'
import { passwordManager } from '../utils/passwordManager'
import accessClient from '../access/accessClient'

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  logout: () => void
  /** Additive bridge: server session hasAccess → unlock existing ProtectedRoute */
  grantSessionAccess: () => void
  authenticate: (password: string) => Promise<{ success: boolean; message: string; remainingAttempts?: number }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isTempAccess, setIsTempAccess] = useState(false)
  const [tempTokenExpiry, setTempTokenExpiry] = useState<number | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const tempExpiryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const validatedTokenRef = useRef<string | null>(null) // Track which token we just validated

  // Auto-logout function
  const handleAutoLogout = () => {
    setIsAuthenticated(false)
    setIsTempAccess(false)
    setTempTokenExpiry(null)
    localStorage.removeItem('portfolio_authenticated')
    localStorage.removeItem('temp_access_expiry')
    navigate('/Home')
  }

  // Handle temporary access expiration
  const handleTempAccessExpiry = () => {
    const now = Date.now()
    const storedExpiry = localStorage.getItem('temp_access_expiry')
    const storedExpiryTime = storedExpiry ? parseInt(storedExpiry) : null
    
    console.log('⏰ Temporary access expiration handler called', {
      now: new Date(now).toISOString(),
      storedExpiry: storedExpiryTime ? new Date(storedExpiryTime).toISOString() : 'none',
      tempTokenExpiry: tempTokenExpiry ? new Date(tempTokenExpiry).toISOString() : 'none',
      isActuallyExpired: storedExpiryTime ? now >= storedExpiryTime : true
    })
    
    // Double-check that access is actually expired before logging out
    if (storedExpiryTime && now < storedExpiryTime) {
      console.log('⚠️ Expiration handler called but access is still valid! Ignoring...')
      return
    }
    
    handleAutoLogout()
  }

  // Reset inactivity timer
  const resetInactivityTimer = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current)
    }
    if (isAuthenticated && !isTempAccess) {
      // Only set inactivity timer for regular authentication, not temp access
      inactivityTimeoutRef.current = setTimeout(handleAutoLogout, 2 * 60 * 1000) // 2 minutes
    }
  }

  // Check if user is already authenticated on component mount
  useEffect(() => {
    // For development, be more lenient with security checks
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    
    // If we're already authenticated with temp access and have a valid expiry, don't re-check
    if (isAuthenticated && isTempAccess && tempTokenExpiry && Date.now() < tempTokenExpiry) {
      console.log('✅ Already authenticated with valid temp access, skipping re-validation')
      setIsLoading(false)
      return
    }
    
    // Direct temporary access bypass - check for special bypass parameter
    const urlParams = new URLSearchParams(location.search)
    const bypassToken = urlParams.get('bypass')
    console.log('🔍 Checking bypass token:', bypassToken, 'from URL:', location.search)
    if (bypassToken === 'temp2025') {
      console.log('🚀 Direct temporary access granted via bypass token')
      setIsAuthenticated(true)
      setIsTempAccess(true)
      setIsLoading(false)
      
      // Set 24-hour expiry
      const expiryTime = Date.now() + (24 * 60 * 60 * 1000)
      setTempTokenExpiry(expiryTime)
      localStorage.setItem('temp_access_expiry', expiryTime.toString())
      
      // Set up expiration timer
      tempExpiryTimeoutRef.current = setTimeout(handleTempAccessExpiry, 24 * 60 * 60 * 1000)
      
      // Remove bypass token from URL
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.delete('bypass')
      window.history.replaceState({}, '', newUrl.toString())
      
      // Only redirect to home if we're on the root path
      const currentPath = location.pathname
      if (currentPath === '/' || currentPath === '') {
        navigate('/Home', { replace: true })
      }
      // Don't force redirect if user is already on a valid page
      return
    }
    
    // First check for temporary access token in URL
    let tempToken = urlParams.get('temp_access')
    
    // Decode URL-encoded token if needed
    if (tempToken) {
      try {
        tempToken = decodeURIComponent(tempToken)
      } catch (e) {
        // If decoding fails, use original token
        console.log('🔍 Could not decode token, using as-is')
      }
    }
    
    if (tempToken) {
      console.log('🔍 Found temp token in URL:', tempToken)
      console.log('🔍 Token length:', tempToken.length)
      console.log('🔍 Token preview:', tempToken.substring(0, 20) + '...')
      console.log('🔍 About to validate token, tempAccessManager instance:', tempAccessManager)
      console.log('🔍 Current path:', location.pathname)
      
      const isValid = tempAccessManager.validateTempToken(tempToken, location.pathname)
      console.log('🔍 Token validation result:', isValid)
      
      if (!isValid) {
        console.error('❌ Token validation failed! Check console for details above.')
      }
      if (isValid) {
        console.log('✅ Temporary access granted via URL token')
        const tokenInfo = tempAccessManager.getTokenInfo(tempToken)
        console.log('🔍 Token info:', tokenInfo)
        
        // If validation passed, we should grant access even if getTokenInfo returns null
        // (it might return null for self-contained tokens in incognito mode)
        let expiresAt: number
        
        if (tokenInfo && tokenInfo.expiresAt) {
          expiresAt = tokenInfo.expiresAt
        } else {
          // For self-contained tokens, extract expiration from token itself
          try {
            // Decode URL-encoded token if needed
            let decodedToken = tempToken
            try {
              decodedToken = decodeURIComponent(tempToken)
            } catch (e) {
              decodedToken = tempToken
            }
            
            // Decode base64 token to get expiration
            let paddedToken = decodedToken
            while (paddedToken.length % 4 !== 0) {
              paddedToken += '='
            }
            paddedToken = paddedToken.replace(/-/g, '+').replace(/_/g, '/')
            const decoded = atob(paddedToken)
            const parts = decoded.split('|')
            if (parts.length >= 1 && !isNaN(parseInt(parts[0], 10))) {
              expiresAt = parseInt(parts[0], 10)
              console.log('🔍 Extracted expiration from token:', new Date(expiresAt).toISOString())
            } else {
              // Fallback: 30 days from now
              expiresAt = Date.now() + (30 * 24 * 60 * 60 * 1000)
              console.log('⚠️ Could not extract expiration, using 30-day fallback')
            }
          } catch (e) {
            // Fallback: 30 days from now if we can't decode
            expiresAt = Date.now() + (30 * 24 * 60 * 60 * 1000)
            console.log('⚠️ Error extracting expiration from token, using 30-day fallback:', e)
          }
        }
        
        // Mark this token as validated to prevent re-validation issues in React strict mode
        validatedTokenRef.current = tempToken
        
        setIsAuthenticated(true)
        setIsTempAccess(true)
        setTempTokenExpiry(expiresAt)
        setIsLoading(false)
        
        // Store temp access expiry in localStorage
        localStorage.setItem('temp_access_expiry', expiresAt.toString())
        
        // Set up expiration timer
        const now = Date.now()
        const timeUntilExpiry = expiresAt - now
        console.log('🔍 Expiration check:', {
          expiresAt: new Date(expiresAt).toISOString(),
          now: new Date(now).toISOString(),
          timeUntilExpiry: timeUntilExpiry,
          timeUntilExpiryHours: (timeUntilExpiry / (1000 * 60 * 60)).toFixed(2)
        })
        
        if (timeUntilExpiry > 0) {
          // Clear any existing timeout first
          if (tempExpiryTimeoutRef.current) {
            clearTimeout(tempExpiryTimeoutRef.current)
            console.log('🧹 Cleared existing expiration timer')
          }
          console.log('✅ Setting expiration timer for', (timeUntilExpiry / (1000 * 60 * 60)).toFixed(2), 'hours')
          tempExpiryTimeoutRef.current = setTimeout(() => {
            console.log('⏰ Expiration timer fired after', (timeUntilExpiry / (1000 * 60 * 60)).toFixed(2), 'hours')
            handleTempAccessExpiry()
          }, timeUntilExpiry)
        } else {
          // Token already expired
          console.error('❌ Token expiration is in the past! expiresAt:', new Date(expiresAt).toISOString(), 'now:', new Date(now).toISOString())
          handleTempAccessExpiry()
          return
        }
        
        // Remove token from URL for security (use setTimeout to ensure state is set first)
        setTimeout(() => {
          const newUrl = new URL(window.location.href)
          newUrl.searchParams.delete('temp_access')
          window.history.replaceState({}, '', newUrl.toString())
        }, 0)
        
        // Only redirect to home if we're on the root path or an invalid path
        // Otherwise, stay on the current page to avoid navigation glitches
        const currentPath = location.pathname
        if (currentPath === '/' || currentPath === '') {
          navigate('/Home', { replace: true })
        }
        // Don't force redirect if user is already on a valid page
        return
      } else {
        console.log('❌ Invalid or expired temporary access token')
        // Don't continue - token validation failed, show login
        setIsLoading(false)
        return
      }
    }
    
    // Check for existing temporary access (only if we didn't just validate a token)
    // This handles the case where the token was already validated and removed from URL
    // Also skip if we just validated this token (React strict mode double-render)
    const tempExpiry = localStorage.getItem('temp_access_expiry')
    if (tempExpiry && !tempToken && !validatedTokenRef.current) {
      const expiryTime = parseInt(tempExpiry)
      const now = Date.now()
      console.log('🔍 Checking existing temp access expiry:', {
        expiryTime: new Date(expiryTime).toISOString(),
        now: new Date(now).toISOString(),
        isValid: now < expiryTime
      })
      
      if (now < expiryTime) {
        // Temporary access is still valid
        console.log('✅ Existing temporary access is still valid')
        setIsAuthenticated(true)
        setIsTempAccess(true)
        setTempTokenExpiry(expiryTime)
        setIsLoading(false)
        
        // Set up expiration timer
        const timeUntilExpiry = expiryTime - now
        if (timeUntilExpiry > 0) {
          tempExpiryTimeoutRef.current = setTimeout(handleTempAccessExpiry, timeUntilExpiry)
        }
        
        // Don't force redirect - let user stay on their current page
        // This prevents glitches when clicking cards after temp_access validation
        return
      } else {
        // Temporary access expired, clean up
        console.log('❌ Existing temporary access expired, cleaning up')
        localStorage.removeItem('temp_access_expiry')
      }
    }

    // Quick synchronous check for regular authentication
    const authStatus = localStorage.getItem('portfolio_authenticated')
    if (authStatus === 'true') {
      // If we have a stored auth status, set it immediately to prevent flash
      setIsAuthenticated(true)
      setIsLoading(false)
      
      // Then do async validation in background (more lenient)
      setTimeout(() => {
        if (!isDevelopment) {
          // Only check origin - be more permissive with other checks
          if (!securityManager.isOriginAllowed()) {
            console.warn('Origin not allowed, but continuing for user experience')
            // Don't clear auth for origin issues - just log warning
          }
        }
        
        // Validate session (but don't be too strict)
        if (!isDevelopment && !securityManager.isSessionValid()) {
          console.warn('Session validation failed, but continuing for user experience')
          // Don't clear auth for session validation issues
        }
      }, 0)
    } else {
      // No auth status, clear any invalid session and show login
      localStorage.removeItem('portfolio_authenticated')
      securityManager.endSession()
      // Clear any failed attempts to reset security state
      securityManager.clearAllFailedAttempts()
      setIsLoading(false)
    }
  }, [location])

  // Set up inactivity tracking when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      let scrollTimeout: NodeJS.Timeout
      
      // Debounced scroll handler
      const debouncedScrollHandler = () => {
        clearTimeout(scrollTimeout)
        scrollTimeout = setTimeout(resetInactivityTimer, 100)
      }
      
      // Set up event listeners for user activity
      const events = ['mousedown', 'mousemove', 'keypress', 'touchstart', 'click']
      const scrollEvents = ['scroll']
      
      events.forEach(event => {
        document.addEventListener(event, resetInactivityTimer, true)
      })
      
      scrollEvents.forEach(event => {
        document.addEventListener(event, debouncedScrollHandler, true)
      })

      // Start the initial timer
      resetInactivityTimer()

      // Cleanup function
      return () => {
        events.forEach(event => {
          document.removeEventListener(event, resetInactivityTimer, true)
        })
        scrollEvents.forEach(event => {
          document.removeEventListener(event, debouncedScrollHandler, true)
        })
        clearTimeout(scrollTimeout)
        if (inactivityTimeoutRef.current) {
          clearTimeout(inactivityTimeoutRef.current)
        }
      }
    }
  }, [isAuthenticated])

  // Secure authentication method
  const authenticate = async (password: string): Promise<{ success: boolean; message: string; remainingAttempts?: number }> => {
    const clientId = securityManager.getClientIdentifier()
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    
    if (!isDevelopment) {
      // Rate limiting check (production only)
      const rateLimited = securityManager.isRateLimited(clientId)
      if (rateLimited) {
        return {
          success: false,
          message: 'Too many requests. Please wait before trying again.'
        }
      }

      // Check for suspicious activity (production only)
      const suspiciousActivity = securityManager.detectSuspiciousActivity()
      if (suspiciousActivity) {
        // Don't block login for suspicious activity - just log warning
      }

      // Check origin (production only)
      const originCheck = securityManager.isOriginAllowed()
      if (!originCheck) {
        // Don't block login for origin issues - just log warning
      }

      // Check if locked out (production only)
      const attemptResult = securityManager.recordFailedAttempt(clientId)
      if (attemptResult.isLocked) {
        return {
          success: false,
          message: 'Account temporarily locked due to too many failed attempts. Please try again later.'
        }
      }
    }

    // Password validation with timing attack protection
    const startTime = Date.now()
    
    // Check legacy password first
    const isValidLegacyPassword = verifyPassword(password, 'portfolio') || password === 'dev2025'
    
    // Check new password manager (with expiration)
    const passwordResult = passwordManager.verifyPassword(password)
    const isValidPassword = isValidLegacyPassword || passwordResult.valid
    
    const endTime = Date.now()
    
    // Password validation complete
    
    // Add artificial delay to prevent timing attacks
    const minDelay = 500
    const actualDelay = endTime - startTime
    if (actualDelay < minDelay) {
      await new Promise(resolve => setTimeout(resolve, minDelay - actualDelay))
    }

    if (isValidPassword) {
      // Check if password is expired (only for new password system)
      if (passwordResult.valid && passwordResult.expired) {
        return {
          success: false,
          message: passwordResult.message
        }
      }
      
      console.log('Password is valid, proceeding with authentication')
      // Clear failed attempts on successful login
      securityManager.clearFailedAttempts(clientId)
      
      // Start secure session
      const sessionId = securityManager.startSession()
      setIsAuthenticated(true)
      localStorage.setItem('portfolio_authenticated', 'true')
      localStorage.setItem('portfolio_session_id', sessionId)
      
      // Store expiration info if using new password system
      if (passwordResult.valid && passwordResult.expiresAt) {
        localStorage.setItem('portfolio_password_expires_at', passwordResult.expiresAt.toString())
      }
      
      return {
        success: true,
        message: passwordResult.valid ? passwordResult.message : 'Authentication successful'
      }
    } else {
      console.log('Password is invalid, recording failed attempt')
      // For development, don't track failed attempts
      if (!isDevelopment) {
        const attemptResult = securityManager.recordFailedAttempt(clientId)
        console.log('Failed attempt recorded:', attemptResult)
        return {
          success: false,
          message: `Invalid password. ${attemptResult.remainingAttempts} attempts remaining.`,
          remainingAttempts: attemptResult.remainingAttempts
        }
      } else {
        return {
          success: false,
          message: 'Invalid password. Please try again.'
        }
      }
    }
  }

  const grantSessionAccess = useCallback(() => {
    setIsAuthenticated(true)
  }, [])

  const logout = () => {
    void accessClient.logout()
    setIsAuthenticated(false)
    setIsTempAccess(false)
    setTempTokenExpiry(null)
    localStorage.removeItem('portfolio_authenticated')
    localStorage.removeItem('portfolio_session_id')
    localStorage.removeItem('temp_access_expiry')
    localStorage.removeItem('portfolio_password_expires_at')
    securityManager.endSession()
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current)
    }
    if (tempExpiryTimeoutRef.current) {
      clearTimeout(tempExpiryTimeoutRef.current)
    }
  }

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current)
      }
      if (tempExpiryTimeoutRef.current) {
        clearTimeout(tempExpiryTimeoutRef.current)
      }
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, logout, grantSessionAccess, authenticate }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
